import { LabelSelectorField } from "components/Form/Fields";
import { LabelSelectFieldOption } from "components/Form/FormikControls";
import { getIn, useFormikContext } from "formik";
import { isEqual, sortBy } from "lodash";
import { EMPTY_ARRAY } from "lib";
import React from "react";
import { useSelector } from "react-redux";
import { getBusinesses, getBusinessLabels } from "selectors";
import { getCheckboxFieldName } from "./util";

export function buildBusinessLabelPayload(
  id: string,
  updatedValues: Record<string, unknown>,
  modelObjects: Record<string, unknown>,
): Record<string, unknown> {
  const removeLabels = (updatedValues?.removeLabels as string[]) || EMPTY_ARRAY;
  const addLabels = (updatedValues?.addLabels as string[]) || EMPTY_ARRAY;
  if (!removeLabels.length && !addLabels.length) {
    return {};
  }
  const business = modelObjects[id] as Business;
  const current: string[] = business?.labelIds || EMPTY_ARRAY;
  const next = current
    .filter((lId: string) => !removeLabels.includes(lId))
    .concat(addLabels);
  if (isEqual(sortBy(current), sortBy(next))) {
    return {};
  }
  return { labelIds: next };
}

interface BusinessLabelSelectorProps {
  modelIds: string[];
  name: string;
}

export const BusinessLabelSelector: React.FC<BusinessLabelSelectorProps> = ({
  modelIds,
  name,
}) => {
  const businessLabels = useSelector(getBusinessLabels) as Record<
    string,
    BusinessLabel
  >;
  const businesses: Record<string, Business> = useSelector(getBusinesses);

  const { values } = useFormikContext();
  const isRemove = name === "removeLabels";
  const isSelected = getIn(values, getCheckboxFieldName(name));

  const labelIdsInSelectedBusinesses = new Set<string>();
  modelIds.forEach(id => {
    (businesses[id]?.labelIds || []).forEach(lId =>
      labelIdsInSelectedBusinesses.add(lId),
    );
  });

  const options: LabelSelectFieldOption[] = Object.values(businessLabels)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(bl => ({ label: bl.name, value: bl.id, isMasterLabel: false }))
    .filter(opt => !isRemove || labelIdsInSelectedBusinesses.has(opt.value));

  return (
    <LabelSelectorField
      name={name}
      label={`Select labels to ${isRemove ? "remove" : "add"}`}
      tooltip={
        isRemove
          ? "Labels to be removed from the selected businesses."
          : "Labels to be added to the selected businesses."
      }
      required={false}
      disabled={!isSelected}
      addLabel={null}
      options={options}
      placeHolder={`Click to ${isRemove ? "remove" : "add"} labels`}
    />
  );
};
